/**
 * IndoorSofa API client
 *
 * Typed client matching the IndoorSofa OpenAPI 3.0.3 spec
 * (api-spec.yaml, IndoorSofa API 0.1.0).
 *
 * Base URL is read from VITE_API_BASE_URL env variable and falls back to
 * the Dev server from the OpenAPI spec.
 * Admin endpoints require an API key via VITE_ADMIN_API_KEY (X-Admin-Key header).
 */

// Dev server from api-spec.yaml (servers[0]).
const DEFAULT_BASE_URL =
  "https://1lv5ggk9fk.execute-api.eu-north-1.amazonaws.com";

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  DEFAULT_BASE_URL;
const ADMIN_KEY = import.meta.env.VITE_ADMIN_API_KEY as string | undefined;

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Creditor type. Empty ("") until the creditor chooses one.
 * Type-specific fields (see `Creditor`) depend on this value.
 */
export type CreditorType = "" | "2.1" | "2.2" | "2.3" | "2.4";

/** Types 2.2, 2.4. When claimed is true, basis is required (may be ""). */
export interface ClaimedBasis {
  claimed: boolean;
  basis: string | null;
}

/** Types 2.2, 2.4. When claimed is true, every other field is required. */
export interface PenaltyInterest {
  claimed: boolean;
  /**
   * Two-decimal string with dot separator, e.g. "56.78"
   * (null when not set). Shown to the customer in Finnish format
   * ("56,78").
   */
  amount: string | null;
  /**
   * Two-decimal string with dot separator, e.g. "9.50" (9,50 %)
   * (null when not set).
   */
  interest_rate: string | null;
  basis: string | null;
  claimed_after_bankruptcy: boolean | null;
  /**
   * Date from which the penalty interest has started to accrue
   * (ISO-8601 date, e.g. "2025-03-01"). In the UI this field is shown
   * for types 2.2 and 2.4 when "Vaadin viivästyskorkoa" is checked
   * (independent of the etuoikeutta checkbox); the input displays
   * Finnish date format (e.g. "6.12.2026") and converts to ISO on save.
   */
  starting_date: string | null;
}

/** Types 2.2, 2.3, 2.4. Both values are required non-empty strings. */
export interface Bank {
  bank_account: string;
  bank_account_holder_name: string;
}

export interface Creditor {
  type?: CreditorType;
  name: string;
  email: string;
  no_email?: boolean;
  phone?: string;
  no_phone?: boolean;
  /** Types 2.3, 2.4. Must be a valid email address. */
  material_delivery_email?: string;
  /** Types "", 2.2, 2.4. Map of MT-number to claim amount ("12.34"). */
  claims?: Record<string, string>;
  /** Types 2.2, 2.4. */
  penalty_interest?: PenaltyInterest | null;
  /** Types 2.2, 2.4. */
  priority?: ClaimedBasis | null;
  /** Types 2.2, 2.3, 2.4. */
  not_conditional_amount?: ClaimedBasis | null;
  /** Types 2.2, 2.3, 2.4. */
  bank?: Bank | null;
  /** Types 2.2, 2.3, 2.4. */
  participate_decision_making?: boolean;
  additional_info?: string;
  /** ReadOnly – set via POST /creditor/{id}/approve. Ignored in request bodies. */
  approved?: boolean;
  /** ReadOnly – ISO-8601 UTC timestamp; empty string until approved. */
  approved_at?: string;
}

export interface CreditorCreated {
  id: string;
  accesscode: string;
}

export interface CreateCreditorsRequest {
  group_id: string;
  creditors: Creditor[];
}

export interface InviteSendResult {
  total: number;
  queued_email: number;
  queued_sms: number;
  paper: number;
  skipped: number;
}

export interface InviteStatusReport {
  group_id: string;
  summary: {
    total: number;
    delivered: number;
    sent: number;
    sent_no_receipt: number;
    queued: number;
    bounced_retrying: number;
    needs_paper: number;
    not_invited: number;
  };
  creditors: Array<{
    creditor_id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    email_suppressed: boolean;
    phone_suppressed: boolean;
    invite: {
      channel: "EMAIL" | "SMS" | "PAPER";
      status: "QUEUED" | "SENT" | "DELIVERED" | "PAPER_NEEDED";
      reason: string | null;
      message_id: string | null;
      updated_at: string | null;
    } | null;
    derived_status:
      | "not_invited"
      | "queued"
      | "sent"
      | "sent_no_receipt"
      | "bounced_retrying"
      | "delivered"
      | "needs_paper";
  }>;
}

export interface BatchSendResult {
  batch: string;
  queued: number;
  skipped: number;
}

export interface ApiError {
  error: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

class HttpError extends Error {
  status: number;
  body: ApiError | string;

  constructor(status: number, body: ApiError | string) {
    const message =
      typeof body === "object" && body.error ? body.error : `HTTP ${status}`;
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;

  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    ...init,
  });

  const text = await res.text();
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    body = text;
  }

  if (!res.ok) {
    throw new HttpError(
      res.status,
      typeof body === "object" && body !== null ? (body as ApiError) : text,
    );
  }

  return body as T;
}

function requireAdminKey(): string {
  if (!ADMIN_KEY) {
    throw new Error(
      "Admin API key is not configured. Set VITE_ADMIN_API_KEY in your .env file.",
    );
  }
  return ADMIN_KEY;
}

// ── Access ───────────────────────────────────────────────────────────────────

/**
 * Resolve an access code to a creditor ID.
 * GET /resolve/{accesscode}
 */
export async function resolveAccessCode(
  accesscode: string,
): Promise<string> {
  return request<string>(`/resolve/${encodeURIComponent(accesscode)}`);
}

// ── Creditor (public) ────────────────────────────────────────────────────────

/**
 * Get a creditor by ID.
 * GET /creditor/{id}
 */
export async function getCreditor(id: string): Promise<Creditor> {
  return request<Creditor>(`/creditor/${encodeURIComponent(id)}`);
}

/**
 * Replace a creditor's data (previous version saved to history).
 * PUT /creditor/{id}
 */
export async function updateCreditor(
  id: string,
  creditor: Creditor,
): Promise<Creditor> {
  return request<Creditor>(`/creditor/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(creditor),
  });
}

/**
 * Approve a creditor's data, locking it against further updates.
 * POST /creditor/{id}/approve
 */
export async function approveCreditor(id: string): Promise<Creditor> {
  return request<Creditor>(`/creditor/${encodeURIComponent(id)}/approve`, {
    method: "POST",
  });
}

// ── Admin: Creditors ─────────────────────────────────────────────────────────

/**
 * Create one or more creditors.
 * POST /admin/creditors
 */
export async function adminCreateCreditors(
  request_data: CreateCreditorsRequest,
): Promise<CreditorCreated[]> {
  return request<CreditorCreated[]>("/admin/creditors", {
    method: "POST",
    headers: { "X-Admin-Key": requireAdminKey() },
    body: JSON.stringify(request_data),
  });
}

/**
 * Delete all creditors belonging to a group.
 * DELETE /admin/creditors/{group_id}
 */
export async function adminDeleteCreditors(
  group_id: string,
): Promise<CreditorCreated[]> {
  return request<CreditorCreated[]>(
    `/admin/creditors/${encodeURIComponent(group_id)}`,
    {
      method: "DELETE",
      headers: { "X-Admin-Key": requireAdminKey() },
    },
  );
}

// ── Admin: Invites ───────────────────────────────────────────────────────────

/**
 * Queue invite sending to all creditors in a group.
 * POST /admin/creditors/{group_id}/invites
 */
export async function adminSendInvites(
  group_id: string,
  force?: boolean,
): Promise<InviteSendResult> {
  return request<InviteSendResult>(
    `/admin/creditors/${encodeURIComponent(group_id)}/invites`,
    {
      method: "POST",
      headers: { "X-Admin-Key": requireAdminKey() },
      body: force !== undefined ? JSON.stringify({ force }) : undefined,
    },
  );
}

/**
 * Invite delivery status for every creditor in a group.
 * GET /admin/creditors/{group_id}/invites
 */
export async function adminGetInviteStatuses(
  group_id: string,
): Promise<InviteStatusReport> {
  return request<InviteStatusReport>(
    `/admin/creditors/${encodeURIComponent(group_id)}/invites`,
    {
      headers: { "X-Admin-Key": requireAdminKey() },
    },
  );
}

// ── Admin: Batches ───────────────────────────────────────────────────────────

/**
 * Queue the campaign email to every recipient of a batch.
 * POST /admin/batches/{batch_number}/emails
 */
export async function adminSendEmailBatch(
  batch_number: string,
): Promise<BatchSendResult> {
  return request<BatchSendResult>(
    `/admin/batches/${encodeURIComponent(batch_number)}/emails`,
    {
      method: "POST",
      headers: { "X-Admin-Key": requireAdminKey() },
    },
  );
}

/**
 * Queue the campaign SMS to every recipient of a batch.
 * POST /admin/batches/{batch_number}/sms
 */
export async function adminSendSMSBatch(
  batch_number: string,
): Promise<BatchSendResult> {
  return request<BatchSendResult>(
    `/admin/batches/${encodeURIComponent(batch_number)}/sms`,
    {
      method: "POST",
      headers: { "X-Admin-Key": requireAdminKey() },
    },
  );
}
